import React, { useState, useMemo, useCallback } from "react";
import {
  Search,
  Plus,
  FileText,
  Calendar,
  Edit,
  Trash2,
  Filter,
  Clock,
  User,
  SortAsc,
  MoreVertical,
} from "lucide-react";
import NoteCreateModal from "./Modal/Create";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import NoteEditModal from "./Modal/Edit";

interface Note {
  _id: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    _id: string;
    name: string;
    email?: string;
  };
}

type SortField = "title" | "createdAt" | "updatedAt";
type SortOrder = "asc" | "desc";
type FilterType = "all" | "recent" | "oldest";
interface propsType {
  contact: any;
}

const Notes: React.FC<propsType> = ({ contact }) => {
  const AppService = useAppServices();
  const [searchTerm, setSearchTerm] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortField, setSortField] = useState<SortField>("updatedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedNote, setSelectedNote] = useState<string | null>(null);
  const contactId = contact?._id;
  // ✅ Fetch notes from API
  const getNotes = async () => {
    try {
      setLoading(true);
      const { response, error } = await AppService.note.filter({
        query: `contactId=${contactId}`,
      });
      if (response) {
        setNotes(response.data);
      } else {
        setNotes([]);
      }
    } catch (error) {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };
  let pageLoad = true;
  const onload = () => {
    if (pageLoad) {
      getNotes();
      pageLoad = false;
    }
  };
  React.useEffect(() => {
    onload();
  }, []);

  // ✅ Handle refresh after creation
  const handleRefresh = () => getNotes();

  // ✅ Memoized filtered and sorted notes
  const filteredNotes = useMemo(() => {
    let filtered = notes;

    // Apply search filter
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (note) =>
          note.title.toLowerCase().includes(lower) ||
          note.description?.toLowerCase().includes(lower)
      );
    }

    // Apply time-based filters
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    switch (activeFilter) {
      case "recent":
        filtered = filtered.filter(
          (note) => new Date(note.updatedAt) >= oneWeekAgo
        );
        break;
      case "oldest":
        filtered = filtered.filter(
          (note) => new Date(note.updatedAt) < oneWeekAgo
        );
        break;
      default:
        break;
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === "createdAt" || sortField === "updatedAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue?.toLowerCase() || "";
        bValue = bValue?.toLowerCase() || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [notes, searchTerm, activeFilter, sortField, sortOrder]);

  // ✅ Memoized utility functions
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }, []);

  const getRelativeTime = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  // ✅ Optimized delete handler
  const handleDelete = useCallback(
    async (_id: string) => {
      const result = await Swal.fire({
        title: "Delete Note?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        setSelectedNote(_id);
        try {
          const { response, error } = await AppService.note.delete({
            query: `_id=${_id}`,
          });

          if (response) {
            await Swal.fire({
              title: "Deleted!",
              text: "The note has been deleted.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            getNotes();
          } else {
            throw new Error(error?.message || "Delete failed");
          }
        } catch (err: any) {
          await Swal.fire({
            title: "Error!",
            text: err.message || "Failed to delete note",
            icon: "error",
          });
        } finally {
          setSelectedNote(null);
        }
      }
    },
    [AppService.note, getNotes]
  );

  // ✅ Filter options
  const filterOptions = useMemo(
    () => [
      { key: "all" as FilterType, label: "All", count: notes.length },
      {
        key: "recent" as FilterType,
        label: "Recent",
        count: notes.filter((note) => {
          const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return new Date(note.updatedAt) >= oneWeekAgo;
        }).length,
      },
    ],
    [notes]
  );

  // ✅ Loading skeleton
  const renderSkeleton = () => (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="animate-pulse bg-slate-200 dark:bg-slate-700 rounded-lg p-4"
        >
          <div className="flex gap-3">
            <div className="size-8 bg-slate-300 dark:bg-slate-600 rounded-md flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-slate-300 dark:bg-slate-600 rounded w-1/3" />
              <div className="h-3 bg-slate-300 dark:bg-slate-600 rounded w-1/4" />
              <div className="h-3 bg-slate-300 dark:bg-slate-600 rounded w-full" />
              <div className="h-3 bg-slate-300 dark:bg-slate-600 rounded w-2/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className=" space-y-4">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <FileText className="size-5 text-blue-600" />
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
              Notes
            </h1>
            <span className="text-sm font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">
              {filteredNotes.length}
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Manage and organize your notes
          </p>
        </div>

        <NoteCreateModal handleRefresh={handleRefresh} />
      </div>

      {/* Search, Filter and Sort Section */}
      <div className="flex flex-col gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 size-4" />
          <input
            type="text"
            placeholder="Search notes by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
        </div>

        {/* Filter and Sort Controls */}
        <div className="flex gap-2">
          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={`${sortField}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split("-");
                setSortField(field as SortField);
                setSortOrder(order as SortOrder);
              }}
              className="appearance-none pl-3 pr-8 py-2 text-xs border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="updatedAt-desc">Last Updated</option>
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="title-asc">Title A-Z</option>
              <option value="title-desc">Title Z-A</option>
            </select>
            <SortAsc className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 size-4 pointer-events-none" />
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            {filterOptions.map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  activeFilter === key
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                {label}
                {count > 0 && (
                  <span className="ml-1.5 text-xs bg-slate-200 dark:bg-slate-600 px-1.5 py-0.5 rounded-full">
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notes List */}
      {loading ? (
        renderSkeleton()
      ) : filteredNotes.length > 0 ? (
        <div className="space-y-3">
          {filteredNotes.map((note) => {
            const isDeleting = selectedNote === note._id;

            return (
              <div
                key={note._id}
                className={`group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 shadow-xs hover:shadow-md transition-all duration-200 ${
                  isDeleting ? "opacity-50" : "opacity-100"
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Note Icon */}
                    <div className="flex items-center justify-center size-10 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex-shrink-0">
                      <FileText className="size-4 text-blue-600 dark:text-blue-400" />
                    </div>

                    {/* Note Content */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-white leading-tight line-clamp-1">
                            {note.title}
                          </h3>

                          {/* Meta Information */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mt-1">
                            <span
                              className="flex items-center gap-1.5"
                              title={`Created: ${getRelativeTime(
                                note.createdAt
                              )}`}
                            >
                              <Calendar className="size-3" />
                              {formatDate(note.createdAt)}
                            </span>

                            {note.createdAt !== note.updatedAt && (
                              <span
                                className="flex items-center gap-1.5"
                                title={`Updated: ${getRelativeTime(
                                  note.updatedAt
                                )}`}
                              >
                                <Clock className="size-3" />
                                {formatDate(note.updatedAt)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                          <NoteEditModal
                            editData={note}
                            handleRefresh={getNotes}
                          />

                          <button
                            onClick={() => handleDelete(note._id)}
                            disabled={isDeleting}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete note"
                          >
                            <Trash2 className="size-4 text-red-500" />
                          </button>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-3">
                        {note.description || "No description added."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-16">
          <div className="flex justify-center mb-4">
            <div className="size-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center">
              <FileText className="size-6 text-slate-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            {searchTerm || activeFilter !== "all"
              ? "No notes found"
              : "No notes yet"}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto leading-relaxed">
            {searchTerm
              ? "Try adjusting your search terms or filters"
              : activeFilter !== "all"
              ? "No notes match your current filter"
              : "Create your first note to get started"}
          </p>
          {searchTerm || activeFilter !== "all" ? (
            <button
              onClick={() => {
                setSearchTerm("");
                setActiveFilter("all");
              }}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              Clear filters
            </button>
          ) : (
            <NoteCreateModal handleRefresh={handleRefresh} />
          )}
        </div>
      )}
    </div>
  );
};

export default Notes;
